/**
 * Modale « État du vélo » — <dialog class="product-condition">.
 *
 * Markup : snippets/product-condition-modal.liquid (rendu côté serveur, crawlable).
 * CSS    : bloc stylesheet de sections/mint-modals.liquid.
 *
 * Patron : product-price-detail.js — IIFE, délégation sur document, garde d'attachement
 * unique, état porté PAR le nœud <dialog> et jamais en variable de module, pour survivre
 * à un re-render de section dans l'éditeur de thème.
 *
 * `window.openApModal` est conservé : des appelants inline en dépendent
 * (product-essentials-sticky.liquid, product-essentials-sticky-fmd.liquid,
 * sections/mint-appearance.liquid, et deux rich-texts de templates JSON).
 * Ils passent leur déclencheur — `openApModal(this)` — pour que le focus revienne au bon
 * élément à la fermeture ; sans argument, on retombe sur le badge de la page.
 */
(function () {
  'use strict';

  if (window.__conditionModalBound) return;
  window.__conditionModalBound = true;

  var DIALOG = 'product-condition';
  var FALLBACK = 'product-condition--fallback';
  var BADGE = 'product-single__appearance';

  // Verrou de scroll (iOS-safe), même patron que gallery-lightbox.js : en mobile la modale
  // est plein écran (100dvh) et `overscroll-behavior` ne couvre que le panneau — tout geste
  // sur l'en-tête sticky ou les gouttières scrollait la page derrière.
  var savedScrollY = 0;
  var savedBody = null;

  function lockScroll() {
    // Idempotent : re-sauver l'état alors qu'il est déjà verrouillé capturerait
    // `position: fixed` et casserait la restauration.
    if (savedBody) return;
    savedScrollY = window.scrollY || document.documentElement.scrollTop || 0;
    var b = document.body;
    savedBody = {
      position: b.style.position,
      top: b.style.top,
      left: b.style.left,
      right: b.style.right,
      width: b.style.width,
      overflow: b.style.overflow,
    };
    b.style.position = 'fixed';
    b.style.top = '-' + savedScrollY + 'px';
    b.style.left = '0';
    b.style.right = '0';
    b.style.width = '100%';
    b.style.overflow = 'hidden';
  }

  function unlockScroll() {
    if (!savedBody) return;
    var b = document.body;
    var s = savedBody;
    b.style.position = s.position || '';
    b.style.top = s.top || '';
    b.style.left = s.left || '';
    b.style.right = s.right || '';
    b.style.width = s.width || '';
    b.style.overflow = s.overflow || '';
    savedBody = null;
    // Restauration AVANT que le navigateur traite un éventuel saut d'ancre (#photos) :
    // notre handler de clic s'exécute en phase bubble, l'action par défaut vient après,
    // donc le saut d'ancre écrase bien ce scrollTo. Inverser l'ordre annulerait le saut.
    //
    // `mint.css` pose `scroll-behavior: smooth` sur `html` : sans neutralisation, cette remise
    // en place est ANIMÉE, et comme un `body` en `position: fixed` est peint depuis le haut, la
    // page remonte puis redescend en glissant sous les yeux du lecteur à chaque fermeture
    // (signalé le 2026-09-09 sur le cran d'état du rapport d'inspection, anti-pattern n°31).
    // Même parade que `product-inspection.js` et `greenleaze-location-modal.js` : on coupe le
    // comportement le temps du saut, plutôt que `behavior: 'instant'`, moins bien supporté sur
    // les Safari anciens. Le verrou et la restauration, eux, étaient corrects.
    var html = document.documentElement;
    var previousBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo(0, savedScrollY);
    html.style.scrollBehavior = previousBehavior;
  }

  function findDialog() {
    return document.querySelector('.' + DIALOG);
  }

  // Câblage paresseux de 'close' (cet event NE BULLE PAS, donc indélégable). Flag posé
  // sur le nœud : un re-render crée un dialog vierge, recâblé à sa 1re ouverture.
  function wireDialog(dialog) {
    if (dialog.__conditionWired) return;
    dialog.__conditionWired = true;
    dialog.addEventListener('close', function () {
      onClosed(dialog);
    });
  }

  function track(dialog, trigger) {
    // La location vient du déclencheur : la modale s'ouvre depuis le bloc sticky mais
    // aussi depuis mint-appearance. Coder 'product_sticky' en dur attribuait toutes les
    // ouvertures à la même zone, alors que la fermeture (déléguée dans
    // tracking-ecommerce.liquid) lit `data-cta-location="condition_modal"` du dialog.
    var locEl = trigger && trigger.closest ? trigger.closest('[data-cta-location]') : null;
    var payload = {
      cta_location: (locEl && locEl.getAttribute('data-cta-location')) || 'product_sticky',
      bike_condition: dialog.getAttribute('data-bike-condition') || '',
    };
    if (typeof window.trackEvent === 'function') {
      window.trackEvent('appearance_modal_open', payload);
    } else if (window.dataLayer) {
      window.dataLayer.push(Object.assign({ event: 'appearance_modal_open' }, payload));
    }
  }

  function openModal(trigger) {
    var dialog = findDialog();
    if (!dialog) return;
    if (dialog.__conditionOpen) return;

    // Retour de focus : le déclencheur explicite, sinon le badge de la page.
    dialog.__conditionTrigger = trigger || document.querySelector('.' + BADGE) || null;
    wireDialog(dialog);

    if (typeof dialog.showModal === 'function') {
      try {
        dialog.showModal();
      } catch (e) {
        openFallback(dialog);
      }
    } else {
      openFallback(dialog);
    }
    dialog.__conditionOpen = true;
    lockScroll();

    var title = dialog.querySelector('[id^="ConditionTitle"]') || dialog.querySelector('[tabindex="-1"]');
    if (title && typeof title.focus === 'function') title.focus();

    track(dialog, dialog.__conditionTrigger);
  }

  // Repli : ni top layer ni ::backdrop natifs → le CSS sort le dialog du flux via
  // `.product-condition--fallback[open]`.
  function openFallback(dialog) {
    dialog.classList.add(FALLBACK);
    dialog.setAttribute('open', '');
  }

  function closeModal(dialog) {
    if (!dialog || !dialog.__conditionOpen) return;
    if (dialog.classList.contains(FALLBACK)) {
      dialog.removeAttribute('open');
      dialog.classList.remove(FALLBACK);
      onClosed(dialog);
    } else if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
      onClosed(dialog);
    }
  }

  // Idempotent : couvre Esc natif, .close(), la croix, le clic backdrop et le repli.
  function onClosed(dialog) {
    if (!dialog || !dialog.__conditionOpen) return;
    dialog.__conditionOpen = false;
    unlockScroll();
    var trigger = dialog.__conditionTrigger;
    dialog.__conditionTrigger = null;
    if (trigger && typeof trigger.focus === 'function') trigger.focus();
  }

  document.addEventListener('click', function (e) {
    // Garde : un clic synthétique dont la cible n'est pas un Element (dispatch
    // programmatique sur document, courant chez les scripts tiers) ferait lever
    // closest() et tuerait TOUT ce handler — croix, backdrop et lien photos compris.
    if (!e.target || !e.target.closest) return;

    var opener = e.target.closest('[data-condition-open], .' + BADGE);
    if (opener) {
      openModal(opener);
      return;
    }
    var closer = e.target.closest('[data-condition-close]');
    if (closer) {
      closeModal(closer.closest('.' + DIALOG));
      // « Voir les photos » : on enchaîne sur la galerie plein écran via le bouton
      // [data-fullscreen-btn] de <product-gallery> (son propre chemin d'ouverture, qui
      // porte le lazy-load de la lightbox). Absent quand le produit n'a qu'un média : on
      // laisse alors le href="#photos" faire le saut d'ancre.
      if (closer.hasAttribute('data-condition-photos')) {
        var fullscreen = document.querySelector('[data-product-gallery] [data-fullscreen-btn]');
        if (fullscreen) {
          e.preventDefault();
          // rAF : laisse la fermeture du 1er <dialog> quitter le top layer avant que la
          // lightbox y entre — deux modales empilées sinon.
          requestAnimationFrame(function () {
            fullscreen.click();
          });
        }
      }
      return;
    }
    // Clic backdrop : la cible est le <dialog> lui-même (le ::backdrop en fait partie).
    if (e.target.classList && e.target.classList.contains(DIALOG)) {
      closeModal(e.target);
    }
  });

  // A11y badge état : .product-single__appearance porte role="button" tabindex="0" →
  // activation clavier (Entrée / Espace) en plus du clic.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var badge = e.target.closest && e.target.closest('.' + BADGE);
    if (!badge) return;
    e.preventDefault();
    openModal(badge);
  });

  // Esc en mode repli uniquement : le <dialog> natif gère Esc lui-même (→ 'close').
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.keyCode !== 27) return;
    var fallbackOpen = document.querySelector('.' + FALLBACK + '[open]');
    if (fallbackOpen) closeModal(fallbackOpen);
  });

  window.openApModal = function (trigger) {
    openModal(trigger && trigger.nodeType === 1 ? trigger : null);
  };
  window.closeApModal = function () {
    closeModal(findDialog());
  };
})();
